import React, { useRef, useEffect, useState } from 'react';
import styles from './styles.module.scss';

interface ZipCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  isValid: boolean | null;
  isChecking: boolean;
  onReset?: () => void;
}

const EMPTY_GLYPH = '·';

/**
 * Componente per l'input del CAP con 5 caselle separate
 */
const ZipCodeInput: React.FC<ZipCodeInputProps> = ({
  value,
  onChange,
  isValid,
  isChecking,
  onReset,
}) => {
  const [digits, setDigits] = useState<string[]>(['2', '0', '1', '0', EMPTY_GLYPH]);
  const [activeInputIndex, setActiveInputIndex] = useState<number>(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!value) {
      onChange('2010');
    }
  }, []);

  useEffect(() => {
    if (value) {
      const newDigits = value.padEnd(5, '').split('').slice(0, 5);
      if (!newDigits[4] || newDigits[4] === ' ') {
        newDigits[4] = EMPTY_GLYPH;
      }
      setDigits(newDigits);
    }
  }, [value]);

  const handleDigitChange = (index: number, digitValue: string) => {
    if (digitValue && !/^\d$/.test(digitValue)) {
      return;
    }

    const newDigits = [...digits];
    if (index === 4 && !digitValue) {
      newDigits[index] = EMPTY_GLYPH;
    } else {
      newDigits[index] = digitValue;
    }
    setDigits(newDigits);

    const newValue = newDigits.map((d) => (d === EMPTY_GLYPH ? '' : d)).join('');
    onChange(newValue);

    if (digitValue && index < 4) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (/^\d$/.test(e.key)) {
      e.preventDefault();

      const newDigits = [...digits];
      newDigits[index] = e.key;
      setDigits(newDigits);
      onChange(newDigits.map((d) => (d === EMPTY_GLYPH ? '' : d)).join(''));

      if (index < 4) {
        inputRefs.current[index + 1]?.focus();
      }
      return;
    }

    if (e.key === 'Backspace') {
      if ((digits[index] === EMPTY_GLYPH || !digits[index]) && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else if (digits[index] && digits[index] !== EMPTY_GLYPH) {
        const newDigits = [...digits];
        newDigits[index] = index === 4 ? EMPTY_GLYPH : '';
        setDigits(newDigits);
        onChange(newDigits.map((d) => (d === EMPTY_GLYPH ? '' : d)).join(''));
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 4) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 5);
    const newDigits = pastedData.padEnd(5, '').split('').slice(0, 5);
    if (!newDigits[4]) {
      newDigits[4] = EMPTY_GLYPH;
    }
    setDigits(newDigits);
    onChange(pastedData);

    const lastIndex = Math.min(pastedData.length, 4);
    inputRefs.current[lastIndex]?.focus();
  };

  const handleFocus = (index: number) => {
    setActiveInputIndex(index);
    inputRefs.current[index]?.select();
  };

  const getDigitClassName = (index: number, digit: string) => {
    const classes = [styles.digitInput];
    if (activeInputIndex === index) {
      classes.push(styles.active);
    } else if (digit && digit !== EMPTY_GLYPH) {
      classes.push(styles.filled);
    } else {
      classes.push(styles.empty);
    }
    return classes.join(' ');
  };

  const displayValue = (digit: string) => (digit === EMPTY_GLYPH ? EMPTY_GLYPH : digit);

  const capPreview = digits.map((d) => (d === EMPTY_GLYPH ? '' : d)).join('');

  if (isValid === true && value.length === 5) {
    return (
      <div className={styles.validatedContainer}>
        <div className={styles.validatedRow}>
          {onReset && (
            <button type="button" className={styles.btnReset} onClick={onReset}>
              ← Modifica CAP
            </button>
          )}
          <span className={styles.validCheck}>✓</span>
          {value}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.zipCodeInputContainer}>
      <div className={styles.digitsWrapper}>
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            className={getDigitClassName(index, digit)}
            value={displayValue(digit)}
            onChange={(e) => handleDigitChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={() => handleFocus(index)}
            disabled={isChecking}
            aria-label={`Cifra CAP ${index + 1}`}
          />
        ))}
      </div>
      {capPreview.length > 0 && (
        <span className={styles.capPill}>CAP {capPreview.padEnd(5, '·')}</span>
      )}
    </div>
  );
};

export default ZipCodeInput;
